/**
 * Lazy loaders for status history and complete screening-run history.
 */

import { useEffect, useState } from "react";
import { clinicalTrialsService } from "../api/clinicalTrialsService";
import type {
  EligibilityScreeningHistory,
} from "../api/clinicalTrialsApiTypes";
import type {
  PatientStatusHistoryResponse,
} from "../data-model/eligibilityDetail";

/** Common state for a lazy-loaded history sub-view. */
export interface LazyHistoryState<T> {
  data: T | null;
  loading: boolean;
  error: string;
}

const idleHistoryState = <T>(): LazyHistoryState<T> => ({
  data: null,
  loading: false,
  error: "",
});

/**
 * Clears loading when an in-flight request is aborted (e.g. drawer closed).
 *
 * @param controller - Aborted fetch controller
 * @returns State updater for effect cleanup
 */
function clearLoadingAfterAbort<T>(
  controller: AbortController,
): (previous: LazyHistoryState<T>) => LazyHistoryState<T> {
  return (previous) =>
    previous.loading && controller.signal.aborted
      ? idleHistoryState<T>()
      : previous;
}

/**
 * Load status history only after its right-rail sub-view opens.
 *
 * @param trialId - Trial path parameter
 * @param mrn - Patient path parameter
 * @param active - Whether the status-history view is open
 * @returns Lazy status-history state
 */
export function useEligibilityStatusHistory(
  trialId: string,
  mrn: string,
  active: boolean,
): LazyHistoryState<PatientStatusHistoryResponse> {
  const [state, setState] = useState<
    LazyHistoryState<PatientStatusHistoryResponse>
  >(idleHistoryState);

  useEffect(() => {
    setState(idleHistoryState());
  }, [mrn, trialId]);

  useEffect(() => {
    if (!active) return undefined;
    const controller = new AbortController();
    setState({ data: null, loading: true, error: "" });
    clinicalTrialsService
      .getEligibilityStatusHistory(trialId, mrn, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setState({ data, loading: false, error: "" });
      })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          data: null,
          loading: false,
          error:
            reason instanceof Error
              ? reason.message
              : "Failed to load status history",
        });
      });
    return () => {
      controller.abort();
      setState(clearLoadingAfterAbort(controller));
    };
  }, [active, mrn, trialId]);

  return state;
}

/**
 * Load complete screening history only after its right-rail sub-view opens.
 *
 * @param trialId - Trial path parameter
 * @param mrn - Patient path parameter
 * @param active - Whether the run-history view is open
 * @returns Lazy screening-history state
 */
export function useEligibilityScreeningHistory(
  trialId: string,
  mrn: string,
  active: boolean,
): LazyHistoryState<EligibilityScreeningHistory> {
  const [state, setState] = useState<
    LazyHistoryState<EligibilityScreeningHistory>
  >(idleHistoryState);

  useEffect(() => {
    setState(idleHistoryState());
  }, [mrn, trialId]);

  useEffect(() => {
    if (!active) return undefined;
    const controller = new AbortController();
    setState({ data: null, loading: true, error: "" });
    clinicalTrialsService
      .getEligibilityScreening(trialId, mrn, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setState({ data, loading: false, error: "" });
      })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          data: null,
          loading: false,
          error:
            reason instanceof Error
              ? reason.message
              : "Failed to load screening history",
        });
      });
    return () => {
      controller.abort();
      setState(clearLoadingAfterAbort(controller));
    };
  }, [active, mrn, trialId]);

  return state;
}
